<template>
    <div class="w-full px-12 mt-48 xs:w-1/2 sm:w-1/3">
        <!--     CARD START-->
        <div>
            <div class="p-20 bg-white rounded-md">
                <div class="flex items-center">
                    <div class="flex items-center justify-center flex-shrink-0 w-48 h-48 bg-yellow-200 rounded-full">
                        <div class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                            <IconPersonSVG class="w-24 h-24"/>
                        </div>
                    </div>

                    <span class="ml-12 font-normal title-lg text-primary">{{person.first_name}} {{person.last_name}}</span>
                </div>

                <span v-if="person.personal_code" class="mt-16 self-label">{{ $t('info.identity_code') }}</span>
                <span v-if="person.personal_code"
                      class="text-base font-semibold sm:text-md">{{ person.personal_code }}</span>

                <span v-if="person.personal_code || person.birth_date"
                      class="mt-16 self-label">{{ $t('info.dob') }}</span>
                <span v-if="person.personal_code" class="text-base font-semibold sm:text-md">{{ birthDate }}</span>
                <span v-else-if="person.birth_date"
                      class="text-base font-semibold sm:text-md">{{ formatDate(person.birth_date) }}</span>

                <span v-if="person.country_of_residence"
                      class="mt-16 self-label">{{ $t('info.country_of_residence') }}</span>
                <span v-if="person.country_of_residence"
                      class="text-base font-semibold sm:text-md">{{
                        countries.find((country) => country.country_code === person.country_of_residence)?.country_name
                    }}</span>

                <span v-if="person.language" class="mt-16 self-label">{{ $t('info.language') }}</span>
                <span v-if="person.language"
                      class="text-base font-semibold sm:text-md">{{ languageMap[person.language] || '-' }}</span>

                <span v-if="person.business_email" class="mt-16 self-label">{{ $t('info.business_email') }}</span>
                <span v-if="person.business_email"
                      class="text-base font-semibold sm:text-md">{{ person.business_email }}</span>

                <span v-if="person.business_phone" class="mt-16 self-label">{{ $t('info.business_phone') }}</span>
                <span v-if="person.business_phone"
                      class="text-base font-semibold sm:text-md">{{ person.business_phone }}</span>

                <span v-if="person.business_mobile_phone"
                      class="mt-16 self-label">{{ $t('info.business_mobile_phone') }}</span>
                <span v-if="person.business_mobile_phone"
                      class="text-base font-semibold sm:text-md">{{ person.business_mobile_phone }}</span>

                <span class="mt-16 self-label">{{ $t('info.auth_valid_until') }}</span>
                <span v-if="new Date(person.end_date).getFullYear() - new Date().getFullYear() > 10"
                      class="text-base font-semibold sm:text-md">{{
                        formatDate(person.start_date)
                    }} - ... ({{ $t('info.indefinite_end_date') }})</span>
                <span v-else
                      class="text-base font-semibold sm:text-md">{{ formatDate(person.start_date) }} - {{ formatDate(person.end_date) }}</span>
                <button @click="initModal" :data-open-modal="this.getModalId()"
                        v-if="!isTravelManager && customerInfo.role === 'travel_manager'"
                        class="mt-16 self-btn-plain">
                    <span class="w-16 h-16 ml-0 mr-4 self-btn-plain__icon">
                        <IconPencilSVG/>
                    </span>
                    <span class="self-btn-plain__text">{{ $t('info.modify') }}</span>
                </button>
            </div>
        </div>
        <!--    CARD END-->

        <!--     MODAL START -->
        <div class="micromodal" :id="this.getModalId()" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">{{ person.first_name }}
                            {{ person.last_name }}</h2>

                        <button class="micromodal__close" aria-label="Close modal" data-micromodal-close>
                <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                    <IconXSVG/>
                </span>
                        </button>
                    </header>
                    <div class="micromodal__content">

                        <div class="flex self-form-wrap">
                            <div
                                :class="['w-full self-form-wrap-item', { 'e5-error-choices': invalidCountry }]">
                                <label class="e5-form-label"
                                       for="country_of_residence">{{ $t('info.country_of_residence') }}<span
                                    class="e5-form-field-required">({{ $t('info.required') }})</span><span
                                    class="e5-form-field-required">*</span></label>
                                <div class="relative">
                                    <div class="self-form-select-wrap">
                                        <select name="country_of_residence" id="country_of_residence" multiple
                                                :class="'self-multiselect-cor-' + getModalId()">
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div
                                :class="['w-full self-form-wrap-item', { 'e5-error-choices': invalidLanguage }]">
                                <label class="e5-form-label" for="language">{{ $t('info.language') }}<span
                                    class="e5-form-field-required">({{ $t('info.required') }})</span><span
                                    class="e5-form-field-required">*</span></label>
                                <div class="relative">
                                    <div class="self-form-select-wrap">
                                        <select name="language" id="language" multiple
                                                :class="'self-multiselect-language-' + getModalId()">
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="epost">{{ $t('info.business_email') }}</label>
                                <div class="relative">
                                    <input type="text" id="epost" placeholder="" name="epost" v-model="businessEmail"
                                           class="e5-input">
                                </div>
                            </div>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="phone">{{ $t('info.business_phone') }}</label>
                                <div class="relative">
                                    <input type="text" id="phone" placeholder="" name="phone" v-model="businessPhone"
                                           class="e5-input">
                                </div>
                            </div>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="mobile">{{ $t('info.business_mobile_phone') }}</label>
                                <div class="relative">
                                    <input type="text" id="mobile" placeholder="" name="mobile" v-model="businessMobile"
                                           class="e5-input">
                                </div>
                            </div>
                            <Dob :label="$t('info.valid_until')" :preset-date="formatDate(person.end_date)"
                                 @update:date="updateEndDate" :disable="isIndefinite.length === 1"/>
                            <div class="self-form-wrap-item">
                                <label class="self-form-item-label" for="deleteBtn">&nbsp;</label>
                                <div class="button-container">
                                    <button @click="removeAuthPerson" data-micromodal-close
                                            class="self-btn-primary-alert"
                                            style="white-space: nowrap;" :disabled="isIndefinite.length === 1">
                                        {{ $t('info.remove_auth') }}
                                    <span class="text-yellow-300 self-btn-primary__icon">
                                                <IconXSVG/>
                                            </span>
                                </button>
                                </div>
                            </div>
                            <div class="w-full self-form-wrap-item">
                                <input class="e5-checkbox" type="checkbox"
                                       :id="'authPersonIndefinite_'+ isTravelManager + '_' + person.customer_id"
                                       :name="'authPersonIndefinite_'+ isTravelManager + '_' + person.customer_id"
                                       value="2999-12-31" v-model="isIndefinite">
                                <label class=""
                                       :for="'authPersonIndefinite_'+ isTravelManager + '_' + person.customer_id">
                                    {{ $t('info.indefinite_end_date') }}
                                </label>
                            </div>
                            <div class="flex items-center justify-between px-8 pt-16 w-full">
                                <div>
                                    <button @click="updateAuthPerson" class="self-btn-primary" :disabled="!isFormOk">
                                        {{ $t('info.save') }}
                                        <span class="text-yellow-300 self-btn-primary__icon">
                                            <IconArrowAltSVG/>
                                        </span>
                                    </button>
                                </div>

                                <div>
                                    <button data-micromodal-close="" class="self-btn-plain">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG/>
                                        </span>
                                        <span class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <!--    MODAL END-->
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import IconNotesSVG from '@/assets/static/icons-src/icon-notes.svg'
import IconPencilSVG from '@/assets/static/icons-src/icon-pencil.svg'
import IconPersonSVG from '@/assets/static/icons-src/icon-person.svg'
import IconXSVG from '@/assets/static/icons-src/icon-x.svg'
import IconArrowAltSVG from '@/assets/static/icons-src/icon-arrow-alt.svg'
import Dob from '@/components/info-view-components/Dob.vue'
import UtilsMixin from '@/scripts/utils'
import { IAuthorizedPersonRequest, ITravelManagerOrAuthorizedPerson } from '@/domain/ITravelManagerOrAuthorizedPerson'
import { ICustomer } from '@/domain/ICustomer'
import MicroModal from 'micromodal'
import store from "@/store";
import Choices from "choices.js";
import $ from "jquery";
import { ICountry } from "@/domain/ICountry";

@Component({
    components: {
        IconNotesSVG,
        IconPencilSVG,
        IconPersonSVG,
        IconXSVG,
        IconArrowAltSVG,
        Dob
    },
    mixins: [UtilsMixin]
})
export default class TMorAuthPersonProfileCard extends Vue {
    @Prop(Object) person!: ITravelManagerOrAuthorizedPerson;
    @Prop(Boolean) isTravelManager!: boolean;

    language = null
    countryOfResidence = null
    businessEmail = ''
    businessMobile = ''
    businessPhone = ''
    endDate = ''
    isIndefinite = []
    invalidCountry = false
    invalidLanguage = false
    choicesMap = { cor: { id: "cor", choices: null }, language: { id: "language", choices: null } }
    languageMap = {
        1: this.$t('info.langs.et'),
        2: this.$t('info.langs.en'),
        3: this.$t('info.langs.ru')
    }

    @Watch('$i18n.locale')
    onLocaleChange() {
        this.languageMap = {
            1: this.$t('info.langs.et'),
            2: this.$t('info.langs.en'),
            3: this.$t('info.langs.ru')
        }
    }

    get customerInfo(): ICustomer {
        return this.$store.getters.getCustomerInfo
    }

    get countries(): ICountry[] {
        return store.state.country;
    }

    get birthDate () {
        const personalCode = this.person.personal_code;
        if (personalCode.length === 11) {
            const year = personalCode.substring(1, 3);
            const month = parseInt(personalCode.substring(3, 5));
            const day = parseInt(personalCode.substring(5, 7));

            // Determine the century based on the first digit of the personal code
            let century: string;
            switch (personalCode.charAt(0)) {
            case '1':
            case '2':
                century = '18';
                break;
            case '3':
            case '4':
                century = '19';
                break;
            case '5':
            case '6':
                century = '20';
                break;
            case '7':
            case '8':
                century = '21';
                break;
            default:
                century = 'Invalid';
                break;
            }

            // Construct the birthdate
            const birthDate = new Date(parseInt(century + year), month - 1, day);
            return birthDate.toLocaleDateString('et-EE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
    }

    get isFormOk() {
        return !this.invalidCountry && !this.invalidLanguage && this.countryOfResidence && this.language;
    }

    private initMultiSelects() {
        const cardId = this.getModalId()
        const languages = {
            eesti: 1,
            english: 2,
            russian: 3
        };
        Object.entries(this.choicesMap).forEach(([key, value]) => {
            if (value.choices) {
                value.choices.destroy();
                value.choices = null;
            }
            const selectElement = document.querySelector(`.self-multiselect-${value.id}-${cardId}`) as HTMLSelectElement
            if (selectElement === null) return
            const placeholderText = this.$t('reports.form.choose') as string

            value.choices = new Choices(document.querySelector(`.self-multiselect-${value.id}-${cardId}`), {
                removeItemButton: true,
                itemSelectText: placeholderText,
                maxItemCount: 2,
                maxItemText: this.$t('reports.form.only_one_can_be_selected') as string,
                noChoicesText: this.$t('reports.form.no_choices_to_choose_from') as string,
                allowHTML: true
            });

            value.choices.passedElement.element.addEventListener('change', (event) => {
                const selectedValues = $(event.target).val() as string[]
                if (selectedValues.length > 1) {
                    // Convert the HTMLSelectElement to an array-like object
                    const options = Array.from(selectElement.options);
                    // Find the option with the specified value and remove it
                    const optionToRemove = options.find(option => option.value === selectedValues[0]);
                    if (optionToRemove) {
                        selectElement.removeChild(optionToRemove);
                    }
                    // Update Choices.js instance to reflect the changes
                    value.choices.removeActiveItemsByValue(value.id === 'language' ? parseInt(selectedValues[0]) : selectedValues[0]);
                    selectedValues.splice(0, 1)
                    value.choices.setChoiceByValue(selectedValues)
                }
                if (value.id === 'language') {
                    this.invalidLanguage = selectedValues.length === 0;
                    this.language = parseInt(selectedValues[0]);
                } else {
                    this.invalidCountry = selectedValues.length === 0;
                    this.countryOfResidence = selectedValues[0];
                }
            }, false);
            if (value.id === 'language') {
                value.choices.setChoices(
                    Object.entries(languages).map(([lang, num]) => ({
                        value: num,
                        label: lang,
                        selected: num === parseInt(this.language)
                    })),
                    'value',
                    'label',
                    false
                );
            } else {
                value.choices.setChoices(
                    this.countries.map(country => ({
                        value: country.country_code,
                        label: country.country_name,
                        selected: country.country_code === this.countryOfResidence
                    })),
                    'value',
                    'label',
                    false
                );
            }
        });
    }

    updateEndDate(newEndDate: string) {
        this.endDate = newEndDate;
    }

    // Function to generate unique ID for modal
    getModalId(person: ITravelManagerOrAuthorizedPerson = this.person): string {
        return `modal_tm_${this.isTravelManager}_${person.first_name}_${person.last_name}_${person.personal_code}`;
    }

    async updateAuthPerson() {
        const request: IAuthorizedPersonRequest = {
            customer_id: this.customerInfo.customerid,
            first_name: '',
            last_name: '',
            start_date: '',
            end_date: this.isIndefinite.length === 0 ? this.endDate : this.isIndefinite[0],
            personal_code: '',
            birth_date: '',
            business_email: this.businessEmail,
            business_phone: this.businessPhone,
            business_mobile_phone: this.businessMobile,
            authperson_id: this.person.authperson_id,
            country_of_residence: this.countryOfResidence,
            language: this.language
        }
        const success = await store.dispatch("updateAuthorizedPerson", request)
        if (!success) {
            this.requestErrorFunction()
            MicroModal.close(this.getModalId())
            return
        }
        MicroModal.close(this.getModalId())
        await store.dispatch("updateCustomerInfo")
    }

    async removeAuthPerson() {
        // Set end date to yesterday
        const today = new Date();
        const dayBeforeToday = new Date(today);
        dayBeforeToday.setDate(today.getDate() - 1);

        const year = dayBeforeToday.getFullYear();
        const month = String(dayBeforeToday.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(dayBeforeToday.getDate()).padStart(2, '0');

        this.endDate = `${year}-${month}-${day}`;
        await this.updateAuthPerson()
    }

    private requestErrorFunction() {
        Vue.notify({
            group: 'api',
            title: 'Server error',
            text: 'Error while while processing update authorized person request',
            type: 'warn'
        });
    }

    initModal() {
        this.language = this.person.language !== undefined ? this.person.language : null
        this.countryOfResidence = this.person.country_of_residence !== undefined ? this.person.country_of_residence : null
        this.businessEmail = this.person.business_email !== undefined ? this.person.business_email : ''
        this.businessPhone = this.person.business_phone !== undefined ? this.person.business_phone : ''
        this.businessMobile = this.person.business_mobile_phone !== undefined ? this.person.business_mobile_phone : ''
        this.endDate = this.person.end_date !== undefined ? this.person.end_date : ''
        if (new Date(this.person.end_date).getFullYear() - new Date().getFullYear() > 10) {
            this.isIndefinite = ['2999-12-31']
        } else {
            this.isIndefinite = []
        }
        this.languageMap = {
            1: this.$t('info.langs.et'),
            2: this.$t('info.langs.en'),
            3: this.$t('info.langs.ru')
        }
        this.invalidCountry = false
        this.invalidLanguage = false
        this.initMultiSelects()
    }
}
</script>

<style scoped>
.button-container {
    display: flex;
    flex-wrap: wrap;
}

/* Add a media query to apply styles when the width is less than 700px */
@media (max-width: 700px) {
    .self-form-wrap-item {
        width: 100%; /* Make each form item take full width */
    }

    .button-container {
        width: 100%; /* Make the button container take full width */
    }
}
</style>
